import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRepairContext } from "@/context/RepairContext";
import { Loader2, Phone, Calendar, Hash, FileText } from "lucide-react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { RepairItem } from "@/types";

const RecentRepairsWidget: React.FC = () => {
  const { t } = useTranslation();
  const { repairs } = useRepairContext();
  const [loading, setLoading] = useState(true);
  const [recentRepairs, setRecentRepairs] = useState<RepairItem[]>([]);

  // Status colors for badges
  const statusColors: Record<string, string> = {
    pending:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
    inProgress: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
    completed:
      "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
    cancelled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
    returned:
      "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  };

  useEffect(() => {
    if (repairs) {
      // Get the 5 most recent repairs
      const sorted = [...repairs].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setRecentRepairs(sorted.slice(0, 5));
      setLoading(false);
    }
  }, [repairs]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (recentRepairs.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-4">
        {t("dashboard.noRepairsFound")}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {recentRepairs.map((repair) => (
        <Link
          key={repair.id}
          to={`/repair/${repair.id}`}
          className="block p-3 rounded-lg border border-border hover:bg-muted transition-colors"
        >
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="font-medium text-foreground">
                {repair.customerName}
              </div>
              <div className="flex items-center text-sm text-muted-foreground mt-1">
                <Phone className="h-3 w-3 mr-1" />
                {repair.phoneModel}
              </div>
              {/* Problem Description */}
              <div className="flex items-start text-xs text-muted-foreground mt-1">
                <FileText className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
                <span
                  className="break-words overflow-hidden"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {repair.problemDescription}
                </span>
              </div>
              {repair.ticketNumber && (
                <div className="flex items-center text-xs text-muted-foreground mt-1">
                  <Hash className="h-3 w-3 mr-1" />
                  {t("repair.ticketNumber")}: {repair.ticketNumber}
                </div>
              )}
              <div className="flex items-center text-xs text-muted-foreground mt-1">
                <Calendar className="h-3 w-3 mr-1" />
                {format(new Date(repair.createdAt), "dd/MM/yyyy HH:mm")}
              </div>
            </div>
            <Badge className={statusColors[repair.status]}>
              {t(`repair.status.${repair.status}`)}
            </Badge>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default RecentRepairsWidget;
